import { useState, useEffect } from 'react';
import { toastMessage } from '@/utils/toast-message';
import Loader from '@/components/common/Loader/Loader';
import Button from '@/components/common/Button';
import { getLeadDetails } from '@/actions/lead-manager';
import { optimizedApi } from '@/utils/api/optimized-api';

interface AdminTrackingOption {
    opt_id: number;
    uuid: string;
    opt_name: string;
    opt_value: string;
}

interface AdminTrackingField {
    col_id: number;
    col_uuid: string;
    col_label: string;
    options: AdminTrackingOption[];
}

interface AdminTrackingOptions {
    admin_status?: AdminTrackingField;
    admin_sub_status?: AdminTrackingField;
    admin_curriculum?: AdminTrackingField;
    admin_category?: AdminTrackingField;
    admin_sub_source?: AdminTrackingField;
    admin_admission_planning?: AdminTrackingField;
    admin_source?: AdminTrackingField;
    grade?: AdminTrackingField;
}

interface AdminTrackingData {
    id?: number;
    uuid?: string;
    admin_status?: number;
    admin_status_uuid?: string;
    admin_sub_status?: number;
    admin_sub_status_uuid?: string;
    admin_curriculum?: number;
    admin_curriculum_uuid?: string;
    admin_category?: number;
    admin_category_uuid?: string;
    admin_sub_source?: number;
    admin_sub_source_uuid?: string;
    admin_admission_planning?: number;
    admin_admission_planning_uuid?: string;
    admin_budget?: string;
    admin_admission_date?: string;
    admin_message?: string;
    admin_child_name?: string;
    admin_prospect?: string;
    admin_suggest_area?: string;
    admin_suggest_city?: string;
    admin_alternative_phone?: string;
    admin_is_qualified?: boolean;
    admin_source?: number;
    admin_source_uuid?: string;
    admin_near_by_client?: string;
    admin_form_type?: string;
    admin_grade?: number;
    admin_grade_uuid?: string;
    adminStatusOption?: { opt_name: string };
    adminSubStatusOption?: { opt_name: string };
    adminCurriculumOption?: { opt_name: string };
    adminCategoryOption?: { opt_name: string };
    adminSubSourceOption?: { opt_name: string };
    adminAdmissionPlanningOption?: { opt_name: string };
    adminSourceOption?: { opt_name: string };
}

interface FormValues {
    admin_status: string;
    admin_sub_status: string;
    admin_curriculum: string;
    admin_category: string;
    admin_sub_source: string;
    admin_admission_planning: string;
    admin_source: string;
    admin_grade: string;
    admin_budget: string;
    admin_admission_date: string;
    admin_message: string;
    admin_child_name: string;
    admin_prospect: string;
    admin_suggest_area: string;
    admin_suggest_city: string;
    admin_alternative_phone: string;
    admin_near_by_client: string;
    admin_form_type: string;
    admin_is_qualified: boolean;
    remark: string;
}

interface AdminTrackingFormProps {
    leadUuid: string;
    onSubmitSuccess?: () => void;
    onClose?: () => void;
}

const STATUS_REMARK_TEMPLATES: Record<string, string> = {
    'Not Answered': 'Parent did not answer the call.',
    'No Response 1': 'Parent did not answer the call.',
    'No Response 2': 'Parent did not answer the call.',
    'Assigned to Client': 'Spoke with the parent and shared the school details. Parent is interested in the XYZ school, so the lead has been assigned to the client.',
    'Not Interested': 'Spoke with the parent, but they are not interested in school admission at the moment.',
    'Not Enquired': 'Parent informed that they are currently not looking for any school admission.',
    'Curriculum Issue': 'Parent is looking specifically for the XYZ curriculum/board, which is currently unavailable.',
    'Employment': 'The caller is looking for a job/employment opportunity.',
    'Invalid Number': "The parent's contact number is invalid or unreachable.",
    "Couldn't Assign": 'Spoke with the parent and suggested nearby client schools, but the parent was not interested in proceeding.',
    'Admitted in Other School': 'Spoke with the parent, but the child has already been admitted to another school.',
    'Callback': 'Parent informed that they are busy and requested a callback later.',
    'Distance': 'Spoke with the parent and suggested nearby client schools, but the distance is not convenient for them.',
    'Fee': 'Spoke with the parent and suggested the XYZ school, but the fee structure is higher than their budget.',
    'Assigned to Claimed': 'Spoke with the parent and shared the school details. Parent is interested in the XYZ school, so the lead has been assigned to the claimed school.',
    'Only in This School': 'Parent is looking specifically for the XYZ school only, which is currently unavailable with us.',
};

const AdminTrackingForm = ({
    leadUuid,
    onSubmitSuccess,
    onClose
}: AdminTrackingFormProps) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [options, setOptions] = useState<AdminTrackingOptions>({});
    const [formValues, setFormValues] = useState<FormValues>({
        admin_status: '',
        admin_sub_status: '',
        admin_curriculum: '',
        admin_category: '',
        admin_sub_source: '',
        admin_admission_planning: '',
        admin_source: '',
        admin_grade: '',
        admin_budget: '',
        admin_admission_date: '',
        admin_message: '',
        admin_child_name: '',
        admin_prospect: '',
        admin_suggest_area: '',
        admin_suggest_city: '',
        admin_alternative_phone: '',
        admin_near_by_client: '',
        admin_form_type: '',
        admin_is_qualified: false,
        remark: '',
    });
    const [remarkError, setRemarkError] = useState<string>('');

    // Fetch options and lead details on mount
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // Fetch options and lead details in parallel
                const [optionsRes, leadRes] = await Promise.all([
                    optimizedApi.get('/leads/admin-tracking/options'),
                    getLeadDetails(leadUuid)
                ]);

                if (optionsRes.success && optionsRes.data) {
                    setOptions(optionsRes.data);
                }

                if (leadRes.success && leadRes.data) {
                    const leadData = leadRes.data as { adminTracking?: AdminTrackingData };
                    const tracking = leadData.adminTracking;

                    // Initialize form values from tracking data
                    if (tracking) {
                        setFormValues({
                            admin_status: tracking.admin_status?.toString() || '',
                            admin_sub_status: tracking.admin_sub_status?.toString() || '',
                            admin_curriculum: tracking.admin_curriculum?.toString() || '',
                            admin_category: tracking.admin_category?.toString() || '',
                            admin_sub_source: tracking.admin_sub_source?.toString() || '',
                            admin_admission_planning: tracking.admin_admission_planning?.toString() || '',
                            admin_source: tracking.admin_source?.toString() || '',
                            admin_grade: tracking.admin_grade?.toString() || '',
                            admin_budget: tracking.admin_budget || '',
                            admin_admission_date: tracking.admin_admission_date || '',
                            admin_message: tracking.admin_message || '',
                            admin_child_name: tracking.admin_child_name || '',
                            admin_prospect: tracking.admin_prospect || '',
                            admin_suggest_area: tracking.admin_suggest_area || '',
                            admin_suggest_city: tracking.admin_suggest_city || '',
                            admin_alternative_phone: tracking.admin_alternative_phone || '',
                            admin_near_by_client: tracking.admin_near_by_client || '',
                            admin_form_type: tracking.admin_form_type || '',
                            admin_is_qualified: tracking.admin_is_qualified || false,
                            remark: '',
                        });
                    }
                }
            } catch (error) {
                console.error('Error fetching admin tracking data:', error);
                toastMessage('Failed to load admin tracking data', 'error');
            } finally {
                setLoading(false);
            }
        };

        if (leadUuid) {
            fetchData();
        }
    }, [leadUuid]);

    const getStatusTemplate = (name: string): string | undefined => {
        const key = Object.keys(STATUS_REMARK_TEMPLATES).find(
            k => k.toLowerCase().trim() === name.toLowerCase().trim()
        );
        return key ? STATUS_REMARK_TEMPLATES[key] : undefined;
    };

    const handleSelectChange = (field: keyof FormValues, value: string) => {
        setFormValues(prev => {
            const updated = { ...prev, [field]: value };
            if (field === 'admin_status') {
                const statusField = options.admin_status;
                const selected = statusField?.options.find(o => o.opt_id.toString() === value);
                console.log('[AdminForm] selected status opt_name:', selected?.opt_name);
                const template = selected ? getStatusTemplate(selected.opt_name) : undefined;
                if (template) {
                    updated.remark = template;
                }
            }
            return updated;
        });
        if (field === 'admin_status' && remarkError) setRemarkError('');
    };

    const handleInputChange = (field: keyof FormValues, value: string | boolean) => {
        setFormValues(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate mandatory remark
        if (!formValues.remark || formValues.remark.trim() === '') {
            setRemarkError('Remark is required when updating admin tracking');
            toastMessage('Please provide a remark', 'error');
            return;
        }
        setRemarkError('');
        setSaving(true);

        try {
            // Find UUID for each select option
            const getOptionUuid = (fieldName: string, optId: string): string | null => {
                const field = options[fieldName as keyof AdminTrackingOptions];
                if (!field || !optId) return null;
                const option = field.options.find(o => o.opt_id.toString() === optId);
                return option?.uuid || null;
            };

            const payload = {
                uuid: leadUuid,
                admin_status: formValues.admin_status ? parseInt(formValues.admin_status) : null,
                admin_status_uuid: getOptionUuid('admin_status', formValues.admin_status),
                admin_sub_status: formValues.admin_sub_status ? parseInt(formValues.admin_sub_status) : null,
                admin_sub_status_uuid: getOptionUuid('admin_sub_status', formValues.admin_sub_status),
                admin_curriculum: formValues.admin_curriculum ? parseInt(formValues.admin_curriculum) : null,
                admin_curriculum_uuid: getOptionUuid('admin_curriculum', formValues.admin_curriculum),
                admin_category: formValues.admin_category ? parseInt(formValues.admin_category) : null,
                admin_category_uuid: getOptionUuid('admin_category', formValues.admin_category),
                admin_sub_source: formValues.admin_sub_source ? parseInt(formValues.admin_sub_source) : null,
                admin_sub_source_uuid: getOptionUuid('admin_sub_source', formValues.admin_sub_source),
                admin_admission_planning: formValues.admin_admission_planning ? parseInt(formValues.admin_admission_planning) : null,
                admin_admission_planning_uuid: getOptionUuid('admin_admission_planning', formValues.admin_admission_planning),
                admin_source: formValues.admin_source ? parseInt(formValues.admin_source) : null,
                admin_source_uuid: getOptionUuid('admin_source', formValues.admin_source),
                admin_grade: formValues.admin_grade ? parseInt(formValues.admin_grade) : null,
                admin_grade_uuid: getOptionUuid('grade', formValues.admin_grade),
                admin_budget: formValues.admin_budget || null,
                admin_admission_date: formValues.admin_admission_date || null,
                admin_message: formValues.admin_message || null,
                admin_child_name: formValues.admin_child_name || null,
                admin_prospect: formValues.admin_prospect || null,
                admin_suggest_area: formValues.admin_suggest_area || null,
                admin_suggest_city: formValues.admin_suggest_city || null,
                admin_alternative_phone: formValues.admin_alternative_phone || null,
                admin_near_by_client: formValues.admin_near_by_client || null,
                admin_form_type: formValues.admin_form_type || null,
                admin_is_qualified: formValues.admin_is_qualified,
                remark: formValues.remark.trim(),
            };

            const response = await optimizedApi.post('/leads/admin-tracking/update', payload);

            if (response.success) {
                toastMessage('Admin tracking updated successfully', 'success');
                // Clear remark after successful save
                setFormValues(prev => ({ ...prev, remark: '' }));
                onSubmitSuccess?.();
            } else {
                throw new Error(response.message || 'Failed to update admin tracking');
            }
        } catch (error: any) {
            console.error('Error updating admin tracking:', error);
            toastMessage(error.message || 'Failed to update admin tracking', 'error');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader />
            </div>
        );
    }

    const renderTableSelectField = (
        label: string,
        fieldName: keyof FormValues,
        optionKey: keyof AdminTrackingOptions
    ) => {
        const field = options[optionKey];
        if (!field) return null;

        return (
            <tr>
                <td className="px-4 py-3 text-sm font-medium text-gray-600 bg-gray-50 w-[140px]">
                    {field.col_label || label}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                    <select
                        value={formValues[fieldName] as string}
                        onChange={(e) => handleSelectChange(fieldName, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-brandColor text-gray-900"
                    >
                        <option value="">Select...</option>
                        {field.options.map((opt) => (
                            <option key={opt.opt_id} value={opt.opt_id.toString()}>
                                {opt.opt_name}
                            </option>
                        ))}
                    </select>
                </td>
            </tr>
        );
    };

    const renderTableTextField = (
        label: string,
        fieldName: keyof FormValues,
        placeholder?: string,
        type: 'text' | 'textarea' = 'text'
    ) => {
        if (type === 'textarea') {
            return (
                <tr>
                    <td className="px-4 py-3 text-sm font-medium text-gray-600 bg-gray-50 w-[140px] align-top">
                        {label}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                        <textarea
                            value={formValues[fieldName] as string}
                            onChange={(e) => handleInputChange(fieldName, e.target.value)}
                            placeholder={placeholder}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-brandColor text-gray-900"
                        />
                    </td>
                </tr>
            );
        }

        return (
            <tr>
                <td className="px-4 py-3 text-sm font-medium text-gray-600 bg-gray-50 w-[140px]">
                    {label}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                    <input
                        type="text"
                        value={formValues[fieldName] as string}
                        onChange={(e) => handleInputChange(fieldName, e.target.value)}
                        placeholder={placeholder}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-brandColor text-gray-900"
                    />
                </td>
            </tr>
        );
    };

    return (
        <form onSubmit={handleSubmit} className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto pb-4">
                <div className="max-w-[50%] min-w-[450px]">
                    {/* Mandatory Remark Field - At Top */}
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-4">
                        <div className="bg-gray-100 px-4 py-3 border-b">
                            <h3 className="text-md font-semibold text-gray-800">
                                Remark <span className="text-red-500">*</span>
                            </h3>
                        </div>
                        <div className="p-4">
                            <textarea
                                value={formValues.remark}
                                onChange={(e) => {
                                    handleInputChange('remark', e.target.value);
                                    if (remarkError) setRemarkError('');
                                }}
                                placeholder="Enter a remark for this update (required)"
                                rows={3}
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brandColor focus:border-brandColor text-gray-900 ${
                                    remarkError ? 'border-red-500' : 'border-gray-300'
                                }`}
                            />
                            {remarkError && (
                                <p className="mt-1 text-sm text-red-500">{remarkError}</p>
                            )}
                        </div>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-100 px-4 py-3 border-b">
                            <h3 className="text-md font-semibold text-gray-800">Admin Tracking Fields</h3>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <tbody className="bg-white divide-y divide-gray-100">
                                    {/* Qualified Checkbox */}
                                    <tr>
                                        <td className="px-4 py-3 text-sm font-medium text-gray-600 bg-gray-50 w-[140px]">
                                            Qualified Lead
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900">
                                            <input
                                                type="checkbox"
                                                id="admin_is_qualified"
                                                checked={formValues.admin_is_qualified}
                                                onChange={(e) => handleInputChange('admin_is_qualified', e.target.checked)}
                                                className="w-4 h-4 text-brandColor border-gray-300 rounded focus:ring-brandColor"
                                            />
                                        </td>
                                    </tr>

                                    {/* Select Fields */}
                                    {renderTableSelectField('Status', 'admin_status', 'admin_status')}
                                    {renderTableSelectField('Sub Status', 'admin_sub_status', 'admin_sub_status')}
                                    {renderTableSelectField('Source', 'admin_source', 'admin_source')}
                                    {renderTableSelectField('Sub Source', 'admin_sub_source', 'admin_sub_source')}
                                    {renderTableSelectField('Grade', 'admin_grade', 'grade')}
                                    {renderTableSelectField('Curriculum', 'admin_curriculum', 'admin_curriculum')}
                                    {renderTableSelectField('Category', 'admin_category', 'admin_category')}
                                    {renderTableSelectField('Admission Planning', 'admin_admission_planning', 'admin_admission_planning')}

                                    {/* Text Fields */}
                                    {renderTableTextField('Near By Client', 'admin_near_by_client', 'yes/no')}
                                    {renderTableTextField('Form Type', 'admin_form_type', 'client/near_client/claimed/other')}
                                    {renderTableTextField('Child Name', 'admin_child_name', 'Enter child name')}
                                    {renderTableTextField('Prospect', 'admin_prospect', 'Enter prospect')}
                                    {renderTableTextField('Budget', 'admin_budget', 'Enter budget')}
                                    {renderTableTextField('Admission Date', 'admin_admission_date', 'Enter admission date')}
                                    {renderTableTextField('Suggest Area', 'admin_suggest_area', 'Enter suggested area')}
                                    {renderTableTextField('Suggest City', 'admin_suggest_city', 'Enter suggested city')}
                                    {renderTableTextField('Alt. Phone', 'admin_alternative_phone', 'Enter alternative phone')}
                                    {renderTableTextField('Message', 'admin_message', 'Enter message', 'textarea')}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Fixed Footer Action Buttons */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 -mx-6 -mb-6">
                <div className="flex justify-start gap-3">
                    {onClose && (
                        <Button
                            type="button"
                            onClick={onClose}
                            variant="outline"
                            disabled={saving}
                        >
                            Cancel
                        </Button>
                    )}
                    <Button
                        type="submit"
                        disabled={saving}
                        loading={saving}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </div>
            </div>
        </form>
    );
};

export default AdminTrackingForm;
